<template>
  <div class="operations-page client-dashboard">
    <header class="client-header">
      <div>
        <h1>{{ pageTitle }}</h1>
        <p>{{ companyLabel }}</p>
      </div>
      <div class="client-export">
        <button
          class="ops-button client-primary"
          :disabled="exporting || !report"
          :aria-busy="String(exporting)"
          @click="download('all')"
        >
          <v-icon aria-hidden="true">{{
            exporting ? 'hourglass_empty' : 'file_download'
          }}</v-icon>
          {{ exporting ? 'Preparando PDF…' : 'Descargar mi operación' }}
        </button>
        <small>PDF con todo el historial desde el inicio</small>
      </div>
    </header>
    <form
      class="client-filters ops-panel"
      aria-label="Período de mi operación"
      @submit.prevent="applyFilters"
    >
      <label
        >Consultar<select
          v-model="filters.mode"
          aria-label="Tipo de período"
          @change="changeMode"
        >
          <option value="month">Por mes</option
          ><option value="range">Rango de fechas</option
          ><option value="all">Desde el inicio</option>
        </select></label
      >
      <template v-if="filters.mode === 'month'">
        <label
          >Mes<select v-model.number="filters.month" aria-label="Mes"
            ><option v-for="(m, i) in months" :key="m" :value="i + 1">{{
              m
            }}</option></select
          ></label
        >
        <label
          >Año<select v-model.number="filters.year" aria-label="Año"
            ><option v-for="y in years" :key="y" :value="y">{{
              y
            }}</option></select
          ></label
        >
      </template>
      <template v-if="filters.mode === 'range'">
        <label
          >Desde<input
            v-model="filters.startDate"
            type="date"
            aria-label="Desde"
            :max="filters.endDate || today"
            required
        /></label>
        <label
          >Hasta<input
            v-model="filters.endDate"
            type="date"
            aria-label="Hasta"
            :min="filters.startDate"
            required
        /></label>
      </template>
      <button class="ops-button" type="submit" :disabled="loading">
        {{ loading ? 'Cargando…' : 'Aplicar' }}
      </button>
      <button
        v-if="report && report.period.mode !== 'all'"
        class="ops-text-button client-period-download"
        type="button"
        :disabled="exporting || loading || !!error"
        @click="download('period')"
      >
        <v-icon small aria-hidden="true">picture_as_pdf</v-icon>Descargar
        período
      </button>
    </form>
    <p v-if="validation" class="client-validation" role="alert">
      {{ validation }}
    </p>
    <div v-if="error" class="ops-error" role="alert">
      <span>{{ error }}</span
      ><button class="ops-button" :disabled="loading" @click="load">
        Reintentar
      </button>
    </div>
    <div v-if="exportError && !detail" class="ops-error" role="alert">
      <span>{{ exportError }}</span
      ><button
        class="ops-button"
        :disabled="exporting"
        @click="download(lastExport.mode, lastExport.id)"
      >
        Reintentar descarga
      </button>
    </div>
    <p v-if="exporting" class="ops-export-status" role="status">
      Preparando el historial completo. Puedes seguir consultando tu operación.
    </p>
    <div
      v-if="!report && loading"
      class="ops-skeleton"
      role="status"
      aria-label="Cargando mi operación"
    >
      <div v-for="n in 5" :key="n" class="ops-skeleton-card"></div>
      <span class="ops-sr-only">Cargando mi operación…</span>
    </div>
    <template v-if="report">
      <div class="ops-period">
        <span
          >{{ periodLabel }} · Corte al {{ date(report.period.asOf)
          }}<span v-if="loading"> · Actualizando…</span></span
        ><span>{{
          report.clientType === 'LAB' ? 'Laboratorio' : 'Cliente distribuidor'
        }}</span>
      </div>
      <template v-if="view === 'inicio'">
        <section class="ops-metrics" aria-label="Resumen de mi operación">
          <button
            v-for="metric in metrics"
            :key="metric.key"
            class="ops-metric"
            :class="'ops-metric--' + metric.tone"
            @click="openList(metric.key, metric.title)"
          >
            <span class="ops-metric-icon"
              ><v-icon aria-hidden="true">{{ metric.icon }}</v-icon></span
            >
            <span class="ops-metric-content"
              ><span class="ops-metric-title">{{ metric.title }}</span
              ><strong>{{
                metric.key === 'enRoute' && !report.logisticsAvailable
                  ? '—'
                  : number(report.totals[metric.key])
              }}</strong
              ><span class="ops-metric-description">{{
                metric.description
              }}</span
              ><span class="ops-metric-link"
                >Ver detalle
                <v-icon small aria-hidden="true">arrow_forward</v-icon></span
              ></span
            >
          </button>
        </section>
        <div class="ops-two-columns">
          <section class="ops-panel client-attention">
            <div class="ops-section-heading">
              <v-icon aria-hidden="true">warning</v-icon>
              <div>
                <h2>Pendientes que requieren mi atención</h2>
                <p>Información que debe aportar mi empresa</p>
              </div>
              <span class="client-count">{{
                number(report.attention.total)
              }}</span>
            </div>
            <div
              v-if="report.attention.items.length"
              class="client-compact-list"
            >
              <button
                v-for="p in report.attention.items"
                :key="p.id"
                @click="openInvoice(p.invoiceId)"
              >
                <strong>{{ p.reference }}</strong
                ><span>{{ p.reason }}</span
                ><v-icon small aria-hidden="true">chevron_right</v-icon>
              </button>
            </div>
            <p v-else class="ops-empty">
              No tienes solicitudes de información asignadas a tu empresa.
            </p>
            <button
              class="ops-text-button"
              @click="openList('pending', 'Mis pendientes')"
            >
              Ver todos los pendientes
              <v-icon small aria-hidden="true">arrow_forward</v-icon>
            </button>
          </section>
          <section class="ops-panel">
            <div class="ops-section-heading">
              <v-icon aria-hidden="true">notifications_active</v-icon>
              <div>
                <h2>Notificaciones recientes</h2>
                <p>Movimientos registrados de mi operación</p>
              </div>
            </div>
            <div
              v-if="report.notifications.items.length"
              class="client-compact-list"
            >
              <button
                v-for="n in report.notifications.items"
                :key="n.id"
                @click="openNotification(n)"
              >
                <v-icon
                  :class="n.kind === 'DENIED' ? 'ops-danger' : 'ops-success'"
                  small
                  aria-hidden="true"
                  >{{ n.kind === 'DENIED' ? 'cancel' : 'check_circle' }}</v-icon
                ><span
                  ><strong>{{ n.reference }}</strong
                  >{{ eventLabel(n.kind)
                  }}<small>{{ date(n.occurredAt, true) }}</small></span
                ><v-icon small aria-hidden="true">chevron_right</v-icon>
              </button>
            </div>
            <p v-else class="ops-empty">
              No hay notificaciones registradas en este período.
            </p>
            <button
              class="ops-text-button"
              @click="openList('notifications', 'Mis notificaciones')"
            >
              Ver notificaciones
              <v-icon small aria-hidden="true">arrow_forward</v-icon>
            </button>
          </section>
        </div>
      </template>

      <section
        v-if="['inicio', 'tramites'].includes(view)"
        class="ops-panel client-tracking"
      >
        <div class="ops-section-heading">
          <v-icon aria-hidden="true">assignment</v-icon>
          <div>
            <h2>Seguimiento de mis trámites</h2>
            <p>Consulta el expediente y su historial desde la recepción</p>
          </div>
        </div>
        <form class="client-search" @submit.prevent="loadInvoices(1)">
          <input
            v-model="term"
            aria-label="Buscar trámite"
            placeholder="Buscar referencia, distribuidor o laboratorio"
            type="search"
          /><button class="ops-button" :disabled="rowsLoading">Buscar</button>
        </form>
        <p v-if="rowsError" class="ops-error" role="alert">
          {{ rowsError }}
          <button class="ops-button" @click="loadInvoices(invoicePage.page)">
            Reintentar lista
          </button>
        </p>
        <div class="ops-table-scroll" :aria-busy="String(rowsLoading)">
          <table class="ops-table client-table">
            <thead>
              <tr>
                <th>Referencia</th>
                <th>Estado</th>
                <th>Pendiente de</th>
                <th>Tiempo hábil</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in invoicePage.items" :key="i.id">
                <td data-label="Referencia">
                  <strong>{{ i.invoiceNumber }}</strong
                  ><small>{{ i.laboratory }}</small>
                </td>
                <td data-label="Estado">
                  <span class="client-state" :class="stateTone(i)">{{
                    i.requiresResponse
                      ? 'Pendiente de información'
                      : stateLabel(i.state)
                  }}</span>
                </td>
                <td data-label="Pendiente de">{{ i.pendingWith }}</td>
                <td data-label="Tiempo hábil">{{ days(i.days) }}</td>
                <td data-label="Acción">
                  <button class="ops-text-button" @click="openInvoice(i.id)">
                    Ver expediente
                    <v-icon small aria-hidden="true">chevron_right</v-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="!invoicePage.items.length">
                <td colspan="5" class="ops-empty">
                  No hay trámites para esta selección.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="client-pagination">
          <span>{{ pageRange(invoicePage) }}</span>
          <div>
            <button
              class="ops-button"
              :disabled="rowsLoading || invoicePage.page <= 1"
              aria-label="Página anterior de trámites"
              @click="loadInvoices(invoicePage.page - 1)"
            >
              Anterior</button
            ><span>{{ invoicePage.page }} / {{ pages(invoicePage) }}</span
            ><button
              class="ops-button"
              :disabled="rowsLoading || invoicePage.page >= pages(invoicePage)"
              aria-label="Página siguiente de trámites"
              @click="loadInvoices(invoicePage.page + 1)"
            >
              Siguiente
            </button>
          </div>
        </div>
        <p class="client-sla-note">
          <v-icon small aria-hidden="true">info_outline</v-icon
          ><span
            >Plazo de aprobación: hasta 3 días hábiles desde la recepción en
            TRANSIT. Alerta desde el día 2 y fuera de rango desde el día 4. Se
            excluyen fines de semana y feriados.</span
          >
        </p>
      </section>

      <div
        v-if="['inicio', 'entregas'].includes(view)"
        class="ops-two-columns client-deliveries"
      >
        <section
          v-for="(s, index) in report.logistics"
          :key="s.type"
          class="ops-panel"
        >
          <div class="ops-section-heading">
            <v-icon aria-hidden="true">{{
              s.type === 'mensajero' ? 'markunread_mailbox' : 'local_shipping'
            }}</v-icon>
            <div>
              <h2>
                {{
                  s.type === 'mensajero' ? 'Mensajería recibida' : 'Mis cargas'
                }}
              </h2>
              <p>
                {{
                  s.type === 'mensajero'
                    ? 'Documentos y gestiones de mensajería'
                    : 'Entregas de transportista'
                }}
              </p>
            </div>
          </div>
          <template v-if="report.logisticsAvailable"
            ><div class="ops-delivery-counts">
              <button
                @click="
                  openList(
                    s.type,
                    s.type === 'mensajero' ? 'Mi mensajería' : 'Mis cargas'
                  )
                "
              >
                <span>Entregados en el período</span
                ><strong>{{ number(s.delivered) }}</strong></button
              ><button
                @click="
                  openList(
                    s.type,
                    s.type === 'mensajero' ? 'Mi mensajería' : 'Mis cargas'
                  )
                "
              >
                <span>Pendientes · {{ s.enRoute }} en camino</span
                ><strong>{{ number(s.pending) }}</strong>
              </button>
            </div>
            <button
              v-if="report.latestServices[index]"
              class="client-service-link"
              @click="
                openList(
                  s.type,
                  s.type === 'mensajero' ? 'Mi mensajería' : 'Mis cargas'
                )
              "
            >
              <span
                >{{ report.latestServices[index].trackingNumber }} ·
                {{ stateLabel(report.latestServices[index].state) }}</span
              ><v-icon small aria-hidden="true">arrow_forward</v-icon>
            </button>
            <p v-else class="ops-empty">
              No hay servicios vinculados para este período.
            </p>
            <p class="client-service-note">
              <v-icon small aria-hidden="true">info_outline</v-icon>La entrega
              confirmada aparece en tus notificaciones.
            </p></template
          >
          <p v-else class="ops-empty">
            El seguimiento de entregas está vinculado a las cuentas de
            distribuidor.
          </p>
        </section>
      </div>

      <section
        v-if="['pendientes', 'documentos', 'notificaciones'].includes(view)"
        class="ops-panel client-workspace"
      >
        <div class="ops-section-heading">
          <v-icon aria-hidden="true">{{ workspaceIcon }}</v-icon>
          <div>
            <h2>{{ pageTitle }}</h2>
            <p>{{ workspaceDescription }}</p>
          </div>
        </div>
        <button
          class="ops-button"
          @click="openList(workspaceSection, pageTitle)"
        >
          Consultar {{ pageTitle.toLowerCase() }}
          <v-icon small aria-hidden="true">arrow_forward</v-icon>
        </button>
      </section>
      <section v-if="view === 'empresa'" class="ops-panel">
        <div class="ops-section-heading">
          <v-icon aria-hidden="true">business</v-icon>
          <div>
            <h2>Mi empresa</h2>
            <p>Empresas vinculadas a tu cuenta</p>
          </div>
        </div>
        <ul class="client-companies">
          <li v-for="c in report.companies" :key="c.id">{{ c.name }}</li>
        </ul>
        <p v-if="!report.companies.length" class="ops-empty">
          Esta cuenta todavía no tiene una empresa vinculada.
        </p>
      </section>
      <p class="ops-data-note">
        Los abiertos y pendientes se muestran al corte, incluidos los de
        períodos anteriores. El PDF del período conserva el historial previo de
        los trámites incluidos.
      </p>
    </template>

    <v-dialog
      :value="!!detail"
      content-class="client-modal-shell"
      :max-width="1050"
      :fullscreen="$vuetify.breakpoint.width <= 600"
      @input="onClose"
    >
      <section
        v-if="detail"
        class="operations-page client-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="detail.title"
      >
        <header class="client-modal-header">
          <div>
            <h2>{{ detail.title }}</h2>
            <p>
              {{
                record
                  ? 'Historial desde el inicio · Corte al ' +
                    date(record.period.asOf)
                  : periodLabel
              }}
            </p>
          </div>
          <button
            ref="closeModal"
            class="ops-icon-button"
            aria-label="Cerrar detalle"
            @click="closeDetail"
          >
            <v-icon aria-hidden="true">close</v-icon>
          </button>
        </header>
        <div class="client-modal-body" :aria-busy="String(detailLoading)">
          <div v-if="exportError" class="ops-error" role="alert">
            <span>{{ exportError }}</span
            ><button
              class="ops-button"
              :disabled="exporting"
              @click="download(lastExport.mode, lastExport.id)"
            >
              Reintentar descarga
            </button>
          </div>
          <p v-if="exporting" class="ops-empty" role="status">
            Preparando el PDF del trámite…
          </p>
          <p v-if="detailLoading" class="ops-empty" role="status">
            Cargando registros…
          </p>
          <div v-if="detailError" class="ops-error" role="alert">
            <span>{{ detailError }}</span
            ><button class="ops-button" @click="retryDetail">
              Reintentar detalle
            </button>
          </div>
          <template v-if="record && record.invoice && !detailLoading">
            <div class="client-record-summary">
              <div>
                <span class="client-state" :class="stateTone(record.invoice)">{{
                  stateLabel(record.invoice.state)
                }}</span>
                <p>
                  {{ record.invoice.distributor }} ·
                  {{ record.invoice.laboratory }}
                </p>
                <small
                  >Recepción {{ date(record.invoice.receivedDate) }} ·
                  {{ days(record.invoice.days) }}</small
                >
              </div>
              <button
                class="ops-button"
                :disabled="exporting"
                @click="download('invoice', record.invoice.id)"
              >
                <v-icon small aria-hidden="true">picture_as_pdf</v-icon> PDF del
                trámite
              </button>
            </div>
            <h3>Historial del trámite</h3>
            <ol class="client-timeline">
              <li v-for="e in record.history" :key="e.id">
                <v-icon small aria-hidden="true">check_circle_outline</v-icon>
                <div>
                  <strong>{{ eventLabel(e.event) }}</strong
                  ><time
                    >{{ date(e.occurredAt, true)
                    }}<span v-if="e.cycle"> · Ciclo {{ e.cycle }}</span></time
                  ><small>{{ e.source }}</small>
                </div>
              </li>
            </ol>
            <h3>Pendientes y documentos solicitados</h3>
            <div v-if="record.pending.length" class="client-record-list">
              <div v-for="p in record.pending" :key="p.id">
                <strong>{{ p.reason }}</strong
                ><span
                  >{{ p.responsible }} ·
                  {{
                    p.resolvedAt
                      ? 'Resuelto el ' + date(p.resolvedAt)
                      : 'Pendiente'
                  }}</span
                ><small>Registrado el {{ date(p.createdAt) }}</small>
              </div>
            </div>
            <p v-else class="ops-empty">Sin pendientes registrados.</p>
            <h3>Documentos registrados</h3>
            <div v-if="record.documents.length" class="client-record-list">
              <div v-for="d in record.documents" :key="d.id">
                <strong>{{ d.originalName || 'Documento registrado' }}</strong
                ><small>{{ date(d.createdAt, true) }}</small>
              </div>
            </div>
            <p v-else class="ops-empty">
              Sin documentos registrados en el expediente.
            </p>
          </template>
          <template v-else-if="record && record.shipment && !detailLoading">
            <span class="client-state client-state--process">{{
              stateLabel(record.shipment.state)
            }}</span>
            <h3>Seguimiento del servicio</h3>
            <ol class="client-timeline">
              <li v-for="e in record.history" :key="e.id">
                <v-icon small aria-hidden="true">local_shipping</v-icon>
                <div>
                  <strong>{{ stateLabel(e.newStatus) }}</strong
                  ><time>{{ date(e.createdAt, true) }}</time>
                </div>
              </li>
            </ol>
            <p v-if="!record.history.length" class="ops-empty">
              Sin movimientos registrados en la bitácora.
            </p>
            <h3>Bitácora de actividades</h3>
            <ol class="client-timeline">
              <li v-for="s in record.steps" :key="s.id">
                <v-icon small aria-hidden="true">{{
                  s.completedAt ? 'check_circle_outline' : 'schedule'
                }}</v-icon>
                <div>
                  <strong>{{ s.position }}. {{ s.title }}</strong
                  ><time>{{
                    s.completedAt ? date(s.completedAt, true) : 'Pendiente'
                  }}</time>
                </div>
              </li>
            </ol>
            <p v-if="!record.steps.length" class="ops-empty">
              Sin actividades registradas.
            </p>
          </template>
          <template v-else-if="!detailLoading && !detailError">
            <form class="client-search" @submit.prevent="loadDetail(1)">
              <input
                v-model="detail.term"
                type="search"
                aria-label="Buscar en detalle"
                placeholder="Buscar referencia o descripción"
              /><button class="ops-button">Buscar</button>
            </form>
            <div v-if="detail.items.length" class="client-detail-list">
              <article
                v-for="(row, i) in detail.items"
                :key="String(row.id) + '-' + i"
              >
                <div>
                  <strong>{{
                    row.invoiceNumber || row.reference || row.trackingNumber
                  }}</strong>
                  <p>
                    {{
                      row.reason ||
                        row.originalName ||
                        (row.kind
                          ? eventLabel(row.kind)
                          : stateLabel(row.state))
                    }}
                  </p>
                  <small>{{
                    row.responsible ||
                      row.pendingWith ||
                      row.clientName ||
                      date(row.occurredAt || row.createdAt, true)
                  }}</small>
                </div>
                <button
                  v-if="row.invoiceId || row.invoiceNumber"
                  class="ops-text-button"
                  @click="openInvoice(row.invoiceId || row.id)"
                >
                  Ver expediente
                  <v-icon small aria-hidden="true"
                    >chevron_right</v-icon
                  ></button
                ><button
                  v-else-if="row.shipmentId || row.trackingNumber"
                  class="ops-text-button"
                  @click="openShipment(row.shipmentId || row.id)"
                >
                  Ver seguimiento
                  <v-icon small aria-hidden="true">chevron_right</v-icon>
                </button>
              </article>
            </div>
            <p v-else class="ops-empty">
              No hay registros para esta selección.
            </p>
          </template>
        </div>
        <footer class="client-modal-footer">
          <span v-if="!record">{{ pageRange(detail) }}</span
          ><span v-else>Solo datos de tu operación</span>
          <div v-if="!record && detail.total > 10">
            <button
              class="ops-button"
              :disabled="detailLoading || detail.page <= 1"
              @click="loadDetail(detail.page - 1)"
            >
              Anterior</button
            ><span>{{ detail.page }} / {{ pages(detail) }}</span
            ><button
              class="ops-button"
              :disabled="detailLoading || detail.page >= pages(detail)"
              @click="loadDetail(detail.page + 1)"
            >
              Siguiente
            </button>
          </div>
          <button v-else class="ops-button" @click="closeDetail">Cerrar</button>
        </footer>
      </section>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import Dashboard from '@/api/Dashboard'
const current = () => moment().tz('America/Panama')
const emptyPage = () => ({ items: [], page: 1, pageSize: 10, total: 0 })
export default {
  name: 'ClientDashboard',
  data() {
    return {
      filters: {
        mode: 'month',
        month: current().month() + 1,
        year: current().year(),
        startDate: current()
          .startOf('month')
          .format('YYYY-MM-DD'),
        endDate: current().format('YYYY-MM-DD'),
      },
      applied: {},
      report: null,
      loading: false,
      error: '',
      validation: '',
      request: 0,
      months: [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
      ],
      term: '',
      invoicePage: emptyPage(),
      rowsLoading: false,
      rowsError: '',
      rowsRequest: 0,
      detail: null,
      record: null,
      detailLoading: false,
      detailError: '',
      detailRequest: 0,
      trigger: null,
      exporting: false,
      exportError: '',
      lastExport: { mode: 'all', id: null },
      timer: null,
      metrics: [
        {
          key: 'processing',
          title: 'Mis trámites en proceso',
          description: 'En curso al corte',
          tone: 'blue',
          icon: 'description',
        },
        {
          key: 'actionRequired',
          title: 'Pendientes de mi respuesta',
          description: 'Requieren mi atención',
          tone: 'orange',
          icon: 'assignment_late',
        },
        {
          key: 'approved',
          title: 'Mis trámites aprobados',
          description: 'Aprobados en el período',
          tone: 'green',
          icon: 'check_circle',
        },
        {
          key: 'denied',
          title: 'Mis trámites rechazados',
          description: 'Rechazados en el período',
          tone: 'red',
          icon: 'cancel',
        },
        {
          key: 'enRoute',
          title: 'Mis entregas en camino',
          description: 'En tránsito al corte',
          tone: 'purple',
          icon: 'local_shipping',
        },
      ],
    }
  },
  computed: {
    today() {
      return current().format('YYYY-MM-DD')
    },
    years() {
      return Array.from(
        { length: current().year() - 1998 },
        (_, i) => current().year() + 1 - i
      )
    },
    view() {
      return [
        'tramites',
        'pendientes',
        'documentos',
        'entregas',
        'notificaciones',
        'empresa',
      ].includes(this.$route.params.view)
        ? this.$route.params.view
        : 'inicio'
    },
    pageTitle() {
      return {
        inicio: 'Dashboard · Cliente',
        tramites: 'Mis trámites',
        pendientes: 'Mis pendientes',
        documentos: 'Mis documentos',
        entregas: 'Mis entregas',
        notificaciones: 'Mis notificaciones',
        empresa: 'Mi empresa',
      }[this.view]
    },
    companyLabel() {
      return this.report
        ? this.report.companies.map(c => c.name).join(' · ') ||
            'Sin empresa vinculada'
        : 'Mi empresa · Mi operación'
    },
    periodLabel() {
      if (!this.report) return ''
      const p = this.report.period
      return p.mode === 'all'
        ? 'Desde el inicio'
        : p.mode === 'month'
        ? this.months[p.month - 1] + ' ' + p.year
        : this.date(p.startDate) + ' al ' + this.date(p.endDate)
    },
    workspaceSection() {
      return {
        pendientes: 'pending',
        documentos: 'documents',
        notificaciones: 'notifications',
      }[this.view]
    },
    workspaceIcon() {
      return {
        pendientes: 'assignment_late',
        documentos: 'folder_open',
        notificaciones: 'notifications_none',
      }[this.view]
    },
    workspaceDescription() {
      return {
        pendientes: 'Solicitudes sin resolver al corte y quién debe responder.',
        documentos: 'Documentos registrados en los expedientes de mi empresa.',
        notificaciones:
          'Aprobaciones, rechazos, reingresos y entregas confirmadas.',
      }[this.view]
    },
  },
  watch: {
    view() {
      this.closeDetail()
      if (this.report && this.workspaceSection)
        this.openList(this.workspaceSection, this.pageTitle)
    },
  },
  created() {
    this.applied = { ...this.filters }
    this.load()
    this.timer = setInterval(() => {
      if (
        !document.hidden &&
        !this.loading &&
        !this.detail &&
        !this.rowsLoading
      )
        this.load(true)
    }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    this.request += 1
    this.rowsRequest += 1
    this.detailRequest += 1
  },
  methods: {
    number(v) {
      return Number(v || 0).toLocaleString('es-PA')
    },
    date(v, time = false) {
      return v
        ? moment
            .tz(v, 'America/Panama')
            .format(time ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
        : 'Sin fecha'
    },
    days(v) {
      return v === null || v === undefined
        ? 'Sin fecha'
        : v + (v === 1 ? ' día' : ' días')
    },
    pages(p) {
      return Math.max(1, Math.ceil(p.total / 10))
    },
    pageRange(p) {
      return p.total
        ? `${(p.page - 1) * 10 + 1}–${Math.min(
            p.page * 10,
            p.total
          )} de ${this.number(p.total)}`
        : 'Sin registros'
    },
    stateLabel(s) {
      return (
        {
          RECEIVED: 'Recibido',
          REINIT: 'Reingreso',
          FD_ADMISSION: 'En trámite',
          APPROVED: 'Aprobado',
          DENIED: 'Rechazado',
          DELIVERED: 'Entregado',
          pendiente: 'Pendiente',
          asignado: 'Asignado',
          en_transito: 'En camino',
          llegada: 'En punto de entrega',
          evidencia: 'Registrando evidencia',
          firma: 'Firma registrada',
          entregado: 'Entregado',
        }[s] ||
        s ||
        'Sin estado'
      )
    },
    eventLabel(e) {
      return (
        {
          REGISTERED: 'Registro en TRANSIT',
          RECEIVED: 'Recepción del trámite',
          CASE_READY: 'Documentación preparada',
          FD_ADMISSION: 'Ingreso a trámite',
          PAYED: 'Pago de impuestos registrado',
          SHIPMENT_DELIVERED: 'Entrega confirmada al cliente',
        }[e] || this.stateLabel(e)
      )
    },
    stateTone(i) {
      return i.requiresResponse
        ? 'client-state--warning'
        : ['APPROVED', 'DELIVERED'].includes(i.state)
        ? 'client-state--success'
        : i.state === 'DENIED'
        ? 'client-state--danger'
        : 'client-state--process'
    },
    changeMode() {
      this.validation = ''
      if (this.filters.mode !== 'range') this.applyFilters()
    },
    applyFilters() {
      if (
        this.filters.mode === 'range' &&
        (!this.filters.startDate ||
          !this.filters.endDate ||
          this.filters.startDate > this.filters.endDate ||
          this.filters.startDate > this.today)
      ) {
        this.validation =
          'Selecciona un rango válido: la fecha inicial debe ser anterior al corte y no posterior a hoy.'
        return
      }
      this.validation = ''
      this.applied = { ...this.filters }
      this.term = ''
      this.closeDetail()
      this.load()
    },
    async load(background = false) {
      const id = ++this.request
      this.rowsRequest += 1
      this.loading = true
      this.rowsLoading = false
      if (!background) this.error = ''
      try {
        const data = await Dashboard.getClient({
          ...this.applied,
          ...(background
            ? { page: this.invoicePage.page, term: this.term }
            : {}),
        })
        if (id !== this.request) return
        this.report = data
        this.invoicePage = data.invoices
        this.rowsError = ''
        this.error = ''
        if (!background && this.workspaceSection)
          this.openList(this.workspaceSection, this.pageTitle)
      } catch (e) {
        if (id === this.request)
          this.error =
            'No se pudo cargar tu operación. Se conserva el último resultado; comprueba la conexión y vuelve a intentar.'
      } finally {
        if (id === this.request) this.loading = false
      }
    },
    scope() {
      const p = this.report.period
      return {
        mode: p.mode,
        month: p.month,
        year: p.year,
        startDate: p.startDate || '',
        endDate: p.endDate,
      }
    },
    async loadInvoices(page) {
      const id = ++this.rowsRequest
      this.rowsLoading = true
      this.rowsError = ''
      try {
        const data = await Dashboard.getClient({
          ...this.scope(),
          section: 'invoices',
          page,
          term: this.term,
        })
        if (id === this.rowsRequest) this.invoicePage = data
      } catch (e) {
        if (id === this.rowsRequest)
          this.rowsError = 'No se pudo cargar la lista de trámites.'
      } finally {
        if (id === this.rowsRequest) this.rowsLoading = false
      }
    },
    async focusModal() {
      await this.$nextTick()
      if (this.$refs.closeModal)
        this.$refs.closeModal.focus({ preventScroll: true })
    },
    openList(section, title) {
      if (!this.detail) this.trigger = document.activeElement
      this.record = null
      this.detail = { ...emptyPage(), section, title, term: '' }
      this.focusModal()
      this.loadDetail(1)
    },
    async loadDetail(page) {
      if (!this.detail) return
      const id = ++this.detailRequest
      this.detailLoading = true
      this.detailError = ''
      try {
        const data = await Dashboard.getClient({
          ...this.scope(),
          section: this.detail.section,
          page,
          term: this.detail.term,
        })
        if (id === this.detailRequest && this.detail)
          Object.assign(this.detail, data)
      } catch (e) {
        if (id === this.detailRequest)
          this.detailError = 'No se pudieron cargar los registros.'
      } finally {
        if (id === this.detailRequest) this.detailLoading = false
      }
    },
    async openInvoice(invoiceId) {
      if (!this.detail) this.trigger = document.activeElement
      const id = ++this.detailRequest
      this.record = null
      this.detail = {
        ...emptyPage(),
        title: 'Expediente del trámite',
        invoiceId,
      }
      this.detailLoading = true
      this.detailError = ''
      this.focusModal()
      try {
        const data = await Dashboard.getClientInvoice(invoiceId)
        if (id !== this.detailRequest || !this.detail) return
        this.record = data
        this.detail.title = 'Expediente · ' + data.invoice.invoiceNumber
      } catch (e) {
        if (id === this.detailRequest)
          this.detailError =
            'No se pudo abrir el expediente o no está disponible para tu cuenta.'
      } finally {
        if (id === this.detailRequest) this.detailLoading = false
      }
    },
    async openShipment(shipmentId) {
      if (!this.detail) this.trigger = document.activeElement
      const id = ++this.detailRequest
      this.record = null
      this.detail = {
        ...emptyPage(),
        title: 'Seguimiento de mi entrega',
        shipmentId,
      }
      this.detailLoading = true
      this.detailError = ''
      this.focusModal()
      try {
        const data = await Dashboard.getClientShipment(shipmentId)
        if (id !== this.detailRequest || !this.detail) return
        this.record = data
        this.detail.title = 'Seguimiento · ' + data.shipment.trackingNumber
      } catch (e) {
        if (id === this.detailRequest)
          this.detailError =
            'No se pudo abrir el servicio o no está disponible para tu cuenta.'
      } finally {
        if (id === this.detailRequest) this.detailLoading = false
      }
    },
    openNotification(n) {
      if (n.invoiceId) this.openInvoice(n.invoiceId)
      else this.openShipment(n.shipmentId)
    },
    retryDetail() {
      if (this.detail.invoiceId) this.openInvoice(this.detail.invoiceId)
      else if (this.detail.shipmentId) this.openShipment(this.detail.shipmentId)
      else this.loadDetail(this.detail.page)
    },
    onClose(visible) {
      if (!visible) this.closeDetail()
    },
    closeDetail() {
      this.detailRequest += 1
      this.detail = null
      this.record = null
      const trigger = this.trigger
      this.$nextTick(() => {
        if (trigger && document.contains(trigger))
          trigger.focus({ preventScroll: true })
      })
    },
    async download(mode, id = null) {
      if (this.exporting || !this.report) return
      const downloadTrigger = document.activeElement
      this.exporting = true
      this.exportError = ''
      this.lastExport = { mode, id }
      const scope =
        mode === 'period'
          ? this.scope()
          : { mode: 'all', ...(id ? { invoiceId: id } : {}) }
      const suffix =
        mode === 'period'
          ? `${scope.startDate}_${scope.endDate}`
          : 'desde-el-inicio'
      try {
        const blob = await Dashboard.downloadClientReport(scope)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `mi-operacion-${
          id ? 'tramite-' + id + '-' : ''
        }${suffix}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        this.exportError =
          'No se pudo descargar el PDF. Comprueba la conexión y vuelve a intentar.'
      } finally {
        this.exporting = false
        await this.$nextTick()
        if (
          downloadTrigger &&
          document.contains(downloadTrigger) &&
          (document.activeElement === document.body || this.detail)
        )
          downloadTrigger.focus({ preventScroll: true })
      }
    },
  },
}
</script>
<style scoped src="./operations-dashboard.css"></style>
<style scoped src="./client-dashboard.css"></style>
<style>
.client-modal-shell.v-dialog {
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.2);
}
.client-modal-shell.v-dialog--fullscreen {
  border-radius: 0;
}
</style>
